import { useEffect, useState } from 'react';
import type { DebtorsReport, MeResponse, OutletRef, ReportPeriod, SalesReport, StockReport, VatMonthReport, VatReport } from '@kassa/shared';
import { api, ApiError } from '../api';
import { downloadReport } from '../api/reports';
import { formatDate, formatMoney, formatQty, hasPermission } from '../format';
import { DateField } from '../components/DateField';
import { PageHeader } from '../components/ListPage';
import { ActTab, ByPeriodTab, CardBankTab, PaymentsTab, StockMovementTab } from './ReportsExtra';

// Bakı təqvimi üzrə bu günün tarixi (YYYY-MM-DD)
function bakuToday(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Baku' });
}

interface Props {
  me: MeResponse;
  onBack: () => void;
}

type Tab = 'sales' | 'payments' | 'cardBank' | 'byPeriod' | 'debtors' | 'act' | 'vat' | 'vatMonth' | 'stock' | 'movement';

const TABS: [Tab, string, string][] = [
  ['sales', 'Satış', 'report.view'],
  ['payments', 'Ödəniş növləri', 'report.view'],
  ['cardBank', 'Kart və bank', 'report.view'],
  ['byPeriod', 'Dövr üzrə', 'report.view'],
  ['debtors', 'Borclular', 'report.view'],
  ['act', 'Akt', 'report.view'],
  ['vat', 'ƏDV', 'report.view'],
  ['vatMonth', 'ƏDV (aylıq)', 'report.finance'],
  ['stock', 'Anbar', 'stock.view'],
  ['movement', 'Anbar hərəkəti', 'stock.view'],
];

// Dövr seçilən tablar (KT-90)
const RANGE_TABS: Tab[] = ['sales', 'payments', 'cardBank', 'byPeriod', 'act', 'vat', 'movement'];
type Preset = ReportPeriod | 'custom';
const PRESETS: [Preset, string][] = [
  ['day', 'Bu gün'],
  ['yesterday', 'Dünən'],
  ['week', 'Bu həftə'],
  ['month', 'Bu ay'],
  ['year', 'Bu il'],
  ['custom', 'Aralıq'],
];

function addDays(day: string, n: number): string {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** Hazır dövrün tarixləri (Bakı) — aralığı məcburi olan hesabatlar üçün (anbar hərəkəti, akt). Server də eyni qaydanı işlədir. */
function presetDates(p: ReportPeriod): { from: string; to: string } {
  const today = bakuToday();
  if (p === 'yesterday') return { from: addDays(today, -1), to: addDays(today, -1) };
  if (p === 'week') return { from: addDays(today, -((new Date(`${today}T00:00:00Z`).getUTCDay() + 6) % 7)), to: today };
  if (p === 'month') return { from: `${today.slice(0, 8)}01`, to: today };
  if (p === 'year') return { from: `${today.slice(0, 4)}-01-01`, to: today };
  return { from: today, to: today };
}

// Aylıq ƏDV hesabatı yalnız vat_report modulu açıq olan obyektdə (Standart və Tam paket) görünür
const MODULE_OF_TAB: Partial<Record<Tab, string>> = { vatMonth: 'vat_report' };

// Hesabatlar (KT-59): dövr (bu gün / bu ay / bu il və ya açıq aralıq), obyekt; hər hesabat Excel-ə ixrac olunur.
export function Reports({ me }: Props) {
  const [openModules, setOpenModules] = useState<string[] | null>(null);
  useEffect(() => {
    api.modules().then((r) => setOpenModules(r.modules.filter((m) => m.open).map((m) => m.code))).catch(() => setOpenModules([]));
  }, []);
  const visible = TABS.filter(
    ([code, , perm]) => hasPermission(me, perm) && (!MODULE_OF_TAB[code] || (openModules ?? []).includes(MODULE_OF_TAB[code]!)),
  );
  const [tab, setTab] = useState<Tab>(visible[0]?.[0] ?? 'stock');
  const [period, setPeriod] = useState<Preset>('month');
  const [from, setFrom] = useState(() => bakuToday().slice(0, 8) + '01');
  const [to, setTo] = useState(bakuToday);
  // Saat (KT-90): istəyə görə — boşdursa bütün gün; son saat daxil deyil (08:00–14:00)
  const [fromTime, setFromTime] = useState('');
  const [toTime, setToTime] = useState('');
  const [outlets, setOutlets] = useState<OutletRef[]>([]);
  const [outletId, setOutletId] = useState('');
  const [sales, setSales] = useState<SalesReport | null>(null);
  const [debtors, setDebtors] = useState<DebtorsReport | null>(null);
  const [vat, setVat] = useState<VatReport | null>(null);
  const [stock, setStock] = useState<StockReport | null>(null);
  const [vatMonth, setVatMonth] = useState<VatMonthReport | null>(null);
  const [month, setMonth] = useState(() => bakuToday().slice(0, 7));
  const [error, setError] = useState<string | null>(null);

  const customFrom = fromTime ? `${from}T${fromTime}` : from;
  const customTo = toTime ? `${to}T${toTime}` : to;
  const range = period === 'custom' ? { from: customFrom, to: customTo } : { period };
  const query = { ...range, outletId: outletId || undefined };
  const explicit = period === 'custom' ? { from: customFrom, to: customTo } : presetDates(period);

  useEffect(() => {
    api.outlets().then(setOutlets).catch(() => setOutlets([]));
  }, []);

  useEffect(() => {
    setError(null);
    const fail = (err: unknown) => setError(err instanceof ApiError ? err.message : 'Hesabat yüklənmədi');
    if (tab === 'sales') api.reportSales(query).then(setSales).catch(fail);
    if (tab === 'debtors') api.reportDebtors().then(setDebtors).catch(fail);
    if (tab === 'vat') api.reportVat(query).then(setVat).catch(fail);
    if (tab === 'stock') api.reportStock(outletId || undefined).then(setStock).catch(fail);
    if (tab === 'vatMonth') api.vatReport(month).then(setVatMonth).catch(fail);
  }, [tab, period, from, to, fromTime, toTime, outletId, month]);

  const excel = (path: string, params: Record<string, string | undefined>, format: 'xlsx' | 'pdf' = 'xlsx') =>
    downloadReport(path, params, format).catch((err: unknown) => setError(err instanceof Error ? err.message : 'Fayl alınmadı'));

  return (
    <main className="page page-list">
      <PageHeader
        title="Hesabatlar"
        tabs={
          <div role="tablist" aria-label="Hesabat növü" className="head-tabs">
            {visible.map(([code, label]) => (
              <button key={code} type="button" role="tab" aria-selected={tab === code} className="head-tab" onClick={() => setTab(code)}>
                {label}
              </button>
            ))}
          </div>
        }
      />
        <section className="card card-wide">
          {RANGE_TABS.includes(tab) && (
            <div className="toolbar report-range">
              {PRESETS.map(([p, label]) => (
                <button key={p} type="button" className={period === p ? 'btn-small is-active' : 'btn-small'} onClick={() => setPeriod(p)}>
                  {label}
                </button>
              ))}
              {period === 'custom' && (
                <>
                  <DateField aria-label="Başlanğıc" value={from} onChange={setFrom} />
                  <input type="time" aria-label="Başlanğıc saatı" value={fromTime} onChange={(e) => setFromTime(e.target.value)} />
                  <span aria-hidden="true">—</span>
                  <DateField aria-label="Son" value={to} onChange={setTo} />
                  <input type="time" aria-label="Son saat (daxil deyil)" value={toTime} onChange={(e) => setToTime(e.target.value)} />
                </>
              )}
              {outlets.length > 1 && tab !== 'act' && (
                <select aria-label="Obyekt" value={outletId} onChange={(e) => setOutletId(e.target.value)}>
                  <option value="">Bütün obyektlər</option>
                  {outlets.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          {error && (
            <p className="bad" role="alert">
              {error}
            </p>
          )}

          {tab === 'sales' && sales && (
            <>
              <div className="toolbar">
                <button type="button" className="btn-small" onClick={() => excel('/api/reports/sales', query)}>
                  Excel-ə
                </button>
              </div>
              <table className="report-table" tabIndex={0} aria-label="Satış hesabatı">
                <thead>
                  <tr>
                    <th>Məhsul</th>
                    <th>Kateqoriya</th>
                    <th className="num">Miqdar</th>
                    <th className="num">Məbləğ</th>
                    <th className="num">Maya</th>
                    <th className="num">Mənfəət</th>
                  </tr>
                </thead>
                <tbody>
                  {sales.byProduct.map((r) => (
                    <tr key={r.productId}>
                      <td>{r.name}</td>
                      <td>{r.category}</td>
                      <td className="num">{formatQty(r.qty)}</td>
                      <td className="num">{formatMoney(r.amount)}</td>
                      <td className="num">{formatMoney(r.cost)}</td>
                      <td className="num">
                        {formatMoney(r.profit)}
                        {r.costUnknown && <span className="badge badge-warn cost-unknown">maya məlum deyil</span>}
                      </td>
                    </tr>
                  ))}
                  {sales.byProduct.length === 0 && (
                    <tr>
                      <td colSpan={6} className="muted">Bu dövrdə satış yoxdur.</td>
                    </tr>
                  )}
                </tbody>
                <tfoot>
                  <tr>
                    <th colSpan={2}>Cəmi</th>
                    <th className="num">{formatQty(sales.totals.qty)}</th>
                    <th className="num">{formatMoney(sales.totals.amount)}</th>
                    <th className="num">{formatMoney(sales.totals.cost)}</th>
                    <th className="num">{formatMoney(sales.totals.profit)}</th>
                  </tr>
                </tfoot>
              </table>
              {sales.totals.unknownCostAmount !== null && sales.totals.unknownCostAmount > 0 && (
                <p className="small vat-warn" role="status">
                  Mayası məlum olmayan satış: {formatMoney(sales.totals.unknownCostAmount)} — bu sətirlərin mənfəəti cəmə daxil deyil (mal qalıqsız satılıb və ya
                  maya 0-la qəbul olunub).
                </p>
              )}
            </>
          )}

          {tab === 'debtors' && debtors && (
            <>
              <div className="toolbar">
                <p className="muted small">{formatDate(debtors.asOf)} tarixinə · yaşlanma açıq borcun yaşına görə</p>
                <button type="button" className="btn-small" onClick={() => excel('/api/reports/debtors', {})}>
                  Excel-ə
                </button>
              </div>
              <table className="report-table" tabIndex={0} aria-label="Borclular">
                <thead>
                  <tr>
                    <th>Müştəri</th>
                    <th className="num">Cəmi borc</th>
                    <th className="num">0–30</th>
                    <th className="num">31–60</th>
                    <th className="num">61–90</th>
                    <th className="num">90+</th>
                  </tr>
                </thead>
                <tbody>
                  {debtors.rows.map((r) => (
                    <tr key={r.customerId}>
                      <td>{r.name}</td>
                      <td className="num">{formatMoney(r.total)}</td>
                      <td className="num">{formatMoney(r.d0_30)}</td>
                      <td className="num">{formatMoney(r.d31_60)}</td>
                      <td className="num">{formatMoney(r.d61_90)}</td>
                      <td className="num">{formatMoney(r.d90)}</td>
                    </tr>
                  ))}
                  {debtors.rows.length === 0 && (
                    <tr>
                      <td colSpan={6} className="muted">Açıq borc yoxdur.</td>
                    </tr>
                  )}
                </tbody>
                <tfoot>
                  <tr>
                    <th>Cəmi</th>
                    <th className="num">{formatMoney(debtors.totals.total)}</th>
                    <th className="num">{formatMoney(debtors.totals.d0_30)}</th>
                    <th className="num">{formatMoney(debtors.totals.d31_60)}</th>
                    <th className="num">{formatMoney(debtors.totals.d61_90)}</th>
                    <th className="num">{formatMoney(debtors.totals.d90)}</th>
                  </tr>
                </tfoot>
              </table>
            </>
          )}

          {tab === 'vat' && vat && (
            <>
              <div className="toolbar">
                <button type="button" className="btn-small" onClick={() => excel('/api/reports/vat', query)}>
                  Excel-ə
                </button>
              </div>
              <ul className="checks">
                <li>
                  <span>Satış (ƏDV-siz)</span>
                  <span>{formatMoney(vat.salesSubtotal)}</span>
                </li>
                <li>
                  <span>Satış ƏDV-si</span>
                  <span>{formatMoney(vat.salesVat)}</span>
                </li>
                <li>
                  <span>Alış ƏDV-si</span>
                  <span>{formatMoney(vat.purchasesVat)}</span>
                </li>
                <li>
                  <strong>Fərq (ödəniləcək)</strong>
                  <strong>{formatMoney(vat.difference)}</strong>
                </li>
              </ul>
            </>
          )}

          {tab === 'vatMonth' && (
            <>
              <div className="toolbar">
                <input type="month" aria-label="Ay" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} />
                <button type="button" className="btn-small" onClick={() => excel('/api/vat/report', { month })}>
                  Excel-ə
                </button>
              </div>
              {vatMonth && <VatMonth r={vatMonth} />}
            </>
          )}

          {tab === 'stock' && stock?.unpriced && stock.unpriced.count > 0 && (
            <p className="small vat-warn" role="status">
              Qiymətsiz qəbul var: {stock.unpriced.count} sənəd, {formatQty(stock.unpriced.qty)} vahid — maya hesabına təsir edir, orta maya müvəqqəti aşağıdır.
            </p>
          )}

          {tab === 'stock' && stock && (
            <>
              <div className="toolbar">
                {outlets.length > 1 && (
                  <select aria-label="Obyekt" value={outletId} onChange={(e) => setOutletId(e.target.value)}>
                    <option value="">Bütün obyektlər</option>
                    {outlets.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.name}
                      </option>
                    ))}
                  </select>
                )}
                <button type="button" className="btn-small" onClick={() => excel('/api/reports/stock', { outletId: outletId || undefined })}>
                  Excel-ə
                </button>
              </div>
              <table className="report-table" tabIndex={0} aria-label="Anbar qalığı">
                <thead>
                  <tr>
                    <th>Məhsul</th>
                    <th>Kateqoriya</th>
                    <th className="num">Fiziki</th>
                    <th className="num">Rezerv</th>
                    <th className="num">Sərbəst</th>
                    <th className="num">Orta maya</th>
                    <th className="num">Dəyər</th>
                  </tr>
                </thead>
                <tbody>
                  {stock.rows.map((r) => (
                    <tr key={r.productId}>
                      <td>{r.name}</td>
                      <td>{r.category}</td>
                      <td className="num">{formatQty(r.qty)}</td>
                      <td className="num">{r.reserved}</td>
                      <td className="num">{r.free}</td>
                      <td className="num">{formatMoney(r.avgCost)}</td>
                      <td className="num">{formatMoney(r.value)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <th colSpan={2}>Cəmi</th>
                    <th className="num">{formatQty(stock.totals.qty)}</th>
                    <th />
                    <th />
                    <th />
                    <th className="num">{formatMoney(stock.totals.value)}</th>
                  </tr>
                </tfoot>
              </table>
            </>
          )}

          {tab === 'payments' && <PaymentsTab query={query} excel={excel} />}
          {tab === 'cardBank' && <CardBankTab query={query} excel={excel} />}
          {tab === 'byPeriod' && <ByPeriodTab query={query} excel={excel} />}
          {tab === 'movement' && <StockMovementTab from={explicit.from} to={explicit.to} outletId={outletId || undefined} excel={excel} />}
          {tab === 'act' && <ActTab from={explicit.from} to={explicit.to} excel={excel} />}

          {visible.length === 0 && <p className="muted">Bu hesabatlar üçün icazəniz yoxdur.</p>}
        </section>
    </main>
  );
}

const MODE_NAME: Record<string, string> = { vat18: '18%', vat0: '0%', exempt: 'Azad', agri: 'K/t (ticarət əlavəsi)' };

/** Aylıq ƏDV hesabatı (KT-66, 2.7). Son sözü mühasib deyir — rəqəmlər sənəd və jurnaldan. */
function VatMonth({ r }: { r: VatMonthReport }) {
  return (
    <>
      <table className="report-table" tabIndex={0} aria-label="ƏDV satış">
        <thead>
          <tr>
            <th>Satış növü</th>
            <th className="num">Dövriyyə (ƏDV daxil)</th>
            <th className="num">Baza</th>
            <th className="num">ƏDV</th>
          </tr>
        </thead>
        <tbody>
          {r.sales.map((s) => (
            <tr key={s.mode}>
              <td>{MODE_NAME[s.mode]}</td>
              <td className="num">{formatMoney(s.turnover)}</td>
              <td className="num">{s.base === null ? '—' : formatMoney(s.base)}</td>
              <td className="num">{formatMoney(s.vat)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {r.agri.fullLines > 0 && <p className="small vat-warn">K/t: {r.agri.fullLines} sətirdə alış şərti pozulub — ƏDV tam məbləğdən hesablanıb.</p>}
      <ul className="checks">
        <li><span>Hesablanmış ƏDV</span><span>{formatMoney(r.outputVat)}</span></li>
        <li><span>Əvəzləşdiriləcək alış ƏDV-si</span><span>{formatMoney(r.offsetVat)}</span></li>
        <li><span>o cümlədən depozitə köçürülməyib (gözləyir)</span><span>{formatMoney(r.offsetPending)}</span></li>
        <li><span>Mayaya düşən ƏDV</span><span>{formatMoney(r.costVat)}</span></li>
        {r.excessVat > 0 && (
          <li><span>Depozitə artıq köçürülmüş (əvəzləşdirilmir)</span><span>{formatMoney(r.excessVat)}</span></li>
        )}
        <li><span>Vergi tutulan dövriyyənin payı</span><span>{r.taxableShare === null ? '—' : `${(r.taxableShare * 100).toFixed(1)}%`}</span></li>
        <li><strong>Ödəniləcək (təxmini)</strong><strong>{formatMoney(r.payable)}</strong></li>
      </ul>
      {r.threshold && (
        <p className={r.threshold.level === 'over' ? 'small vat-over' : r.threshold.level === 'warn' ? 'small vat-warn' : 'muted small'}>
          12 aylıq vergi tutulan dövriyyə ({r.threshold.from} — {r.threshold.to}): {formatMoney(r.threshold.turnover)}
          {r.threshold.level === 'over' && ' — həddi keçib: növbəti ayın 10-dək ƏDV qeydiyyatı ərizəsi.'}
          {r.threshold.level === 'warn' && ' — həddə yaxınlaşır.'}
        </p>
      )}
      {r.offset.length > 0 && (
        <table className="report-table" tabIndex={0} aria-label="Əvəzləşdirilən alışlar">
          <thead>
            <tr>
              <th>Sənəd</th>
              <th>Təchizatçı</th>
              <th>Mənbə nömrəsi</th>
              <th className="num">ƏDV</th>
              <th className="num">Depozitə</th>
              <th className="num">Gözləyir</th>
            </tr>
          </thead>
          <tbody>
            {r.offset.map((o) => (
              <tr key={o.docId}>
                <td className="mono">{o.displayNo}</td>
                <td>{o.supplierName}</td>
                <td>{o.sourceNo ?? '—'}</td>
                <td className="num">{formatMoney(o.vat)}</td>
                <td className="num">{formatMoney(o.deposited)}</td>
                <td className="num">{formatMoney(o.pending)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="muted small">Sistem hesablayır və hesabat verir; bəyannaməni və son qərarı mühasib verir.</p>
    </>
  );
}
